import React, { useEffect, useState } from "react";

import CustomTable from "components/common/CustomTable";
import EmptyStateComp from "components/common/EmptyStateComp";
import SynthTooltip from "components/common/SynthTooltip";
import { formatMoney } from "helpers";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import ResponsivePaginationComponent from "react-responsive-pagination";
import "react-responsive-pagination/themes/classic.css";
import { dashboardActions } from "redux/Ldb/actions";
import { SpaceForecastsTabContentProps } from "./types/SpaceForecastsTabProps";

const SpaceDetailsForecastTabComponentForecast = (props: SpaceForecastsTabContentProps) => {
    const {
        nonFinishedGoodsData,
        nonFinishedGoodsLoading,
        nonFinishedGoodsPagination,
        nonFinishedGoodsSetUpdatedPage,
        setRefreshNonFinishedGoods,
    } = props;

    const [expandedRowIdx, setExpandedRowIdx] = useState<number>(-1);

    const dispatch = useAppDispatch();
    const { fetchPinForecastConfigSuccess, fetchUnpinForecastConfigSuccess } = useAppSelector(
        (state) => state.dashboard
    );

    const componentTableHeader = [
        {
            title: null,
            widthClass: "w-[50px] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">Component</span>

                    <SynthTooltip
                        content={
                            "Individual materials or ingredients used to produce finished goods. Components are tracked separately to ensure accurate forecasting and procurement."
                        }
                        divId={"component-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Current stock level
                    </span>

                    <SynthTooltip
                        content={
                            "The actual quantity of the product currently available in stock for sale or use at this moment."
                        }
                        divId={"current-stock-level-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Forecasted qty in use
                    </span>

                    <SynthTooltip
                        content={
                            "The estimated amount of this component expected to be consumed in the production of finished goods, based on current demand forecasts and planned manufacturing activity."
                        }
                        divId={"forecasted-qty-in-use-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Forecasted needs
                    </span>

                    <SynthTooltip
                        content={
                            "The estimated amount of the product required to meet anticipated demand during the forecast period."
                        }
                        divId={"forecasted-needs-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Stock deficit
                    </span>

                    <SynthTooltip
                        content={
                            "The number of units your inventory is short based on forecasted demand. This highlights how much stock needs to be replenished to meet future needs."
                        }
                        divId={"stock-deficit-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Forecasted cost
                    </span>

                    <SynthTooltip
                        content={
                            "The predicted total cost to fulfill the forecasted demand, based on the product's unit cost and the stock deficit."
                        }
                        divId={"forecasted-cost-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
    ];

    const componentTableBody = nonFinishedGoodsData?.map((item, idx) => {
        const stockDeficit =
            item?.productStockDetails?.stockLevel - item?.forecast?.forecastedNeeds;
        const isPinned = item?.isPinned;

        return [
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 flex justify-center items-center text-xl text-slate-600  py-2`}
                    >
                        <i
                            className={`ri-star-fill text-center cursor-pointer ${
                                isPinned ? "text-[#FCD34D]" : "text-[#E2E8F0] "
                            }`}
                            onClick={() => {
                                if (isPinned) {
                                    handleUnpinInventory(item?._id);
                                } else {
                                    handlePinInventory(item?._id);
                                }
                            }}
                        ></i>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">{item?.sku}</p>

                            <p className="">{item?.productName}</p>
                        </div>
                    </div>
                ),
                expandedRowAction: () => {
                    idx === expandedRowIdx ? setExpandedRowIdx(-1) : setExpandedRowIdx(idx);
                    // setDate(snapshot?.date);
                },
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">
                                {formatMoney()?.format(item?.productStockDetails?.stockLevel)}{" "}
                                {item?.productStockDetails?.unitOfMeasurement || ""}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">
                                {formatMoney()?.format(item?.forecast?.forecast)}{" "}
                                {item?.productStockDetails?.unitOfMeasurement || ""}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p
                                className={`${
                                    item?.forecast?.forecastedNeeds
                                        ? "text-red-500"
                                        : "text-slate-500"
                                } text-xs `}
                            >
                                {item?.forecast?.forecastedNeeds === 0 ? (
                                    <span className="text-green-500">Sufficent</span>
                                ) : (
                                    formatMoney()?.format(item?.forecast?.forecastedNeeds) +
                                    " " +
                                    (item?.productStockDetails?.unitOfMeasurement || "")
                                )}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p
                                className={` text-xs ${
                                    String(stockDeficit)[0] === "-"
                                        ? "text-red-500"
                                        : "text-slate-500"
                                }`}
                            >
                                {String(stockDeficit)[0] === "-" ? (
                                    formatMoney()?.format(String(stockDeficit)?.slice(1)) +
                                    " " +
                                    (item?.productStockDetails?.unitOfMeasurement || "")
                                ) : (
                                    <span className="text-green-500">Sufficent</span>
                                )}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">
                                ${formatMoney()?.format(item?.forecast?.forecastedCost)}
                            </p>
                        </div>
                    </div>
                ),
            },
        ];
    });

    const expandedTableHeader = [
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Associated SKUs/products
                    </span>

                    <SynthTooltip
                        content={
                            "Finished goods (SKUs) that have this particular product as one of its components."
                        }
                        divId={"associated-product-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Forecasted qty
                    </span>

                    <SynthTooltip
                        content={
                            "The estimated number of units expected to be needed or used during the forecast period."
                        }
                        divId={"forecasted-qty-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
    ];

    const expandedTableBody = nonFinishedGoodsData?.[expandedRowIdx]?.inventoriesUsedIn?.map(
        (item, idx) => {
            return [
                {
                    content: (
                        <div
                            key={idx}
                            className={`!font-rocGroteskMedium border-slate-100 text-sm  py-2`}
                        >
                            <div>
                                <p className=" text-sm ">{item?.inventory?.sku}</p>

                                <p className="text-sm text-slate-500">
                                    {item?.inventory?.productName}
                                </p>
                            </div>
                        </div>
                    ),
                },
                {
                    content: (
                        <div
                            key={idx}
                            className={`!font-rocGroteskMedium border-slate-100 text-sm  py-2`}
                        >
                            <div>
                                <p className=" text-sm ">
                                    {formatMoney()?.format(
                                        item?.estinmatedSalesForecast?.forecastedSale
                                    )}{" "}
                                    {nonFinishedGoodsData?.[expandedRowIdx]?.productStockDetails
                                        ?.unitOfMeasurement || ""}
                                </p>
                            </div>
                        </div>
                    ),
                },
            ];
        }
    );

    const handlePinInventory = (inventoryId: string) => {
        dispatch(dashboardActions.pinForecastConfig(inventoryId));
    };

    const handleUnpinInventory = (inventoryId: string) => {
        dispatch(dashboardActions.unpinForecastConfig(inventoryId));
    };

    useEffect(() => {
        if (!!fetchPinForecastConfigSuccess) {
            setRefreshNonFinishedGoods((prevValue) => !prevValue);
        }
    }, [fetchPinForecastConfigSuccess]);

    useEffect(() => {
        if (!!fetchUnpinForecastConfigSuccess) {
            setRefreshNonFinishedGoods((prevValue) => !prevValue);
        }
    }, [fetchUnpinForecastConfigSuccess]);

    return (
        <div className="py-[16px] px-[20px]">
            <div>
                <div>
                    <CustomTable
                        tableBody={componentTableBody}
                        tableHeader={componentTableHeader}
                        isCellBordered={true}
                        isCellSelectable={false}
                        stickyHeader={true}
                        headerContainerClass="!bg-slate-50 !border-slate-200"
                        headerItemClass="font-rocGroteskMedium "
                        tableClass="mb-12 !border-slate-200"
                        isCollapseable={true}
                        isCollapseableIconIndex={1}
                        isLoading={nonFinishedGoodsLoading}
                        expandedRowIdx={expandedRowIdx}
                        // allCellClick={true}
                        // bodyItemClass="!border-slate-200"
                        rowExpanded={
                            <div className="relative z-[1300]">
                                {/* {isLoadingGroup && snapshotsList?.length === 0 ? (
                                          <div className="flex justify-center my-4">
                                              <Loader color="gm-25" size={8} />
                                          </div>
                                      ) : ( */}
                                <div>
                                    <div className="pt-2.5 px-3 bg-slate-50">
                                        {expandedTableBody?.length > 0 ? (
                                            <div className="">
                                                <CustomTable
                                                    tableBody={expandedTableBody}
                                                    tableHeader={expandedTableHeader}
                                                    isCellBordered={true}
                                                    isInifiniteScroll={false}
                                                    // isAllSelectable={true}
                                                    stickyHeader={true}
                                                    headerContainerClass="!bg-slate-50 !border-slate-200"
                                                    headerItemClass="font-rocGroteskMedium "
                                                    tableClass="my-3 !border-t-slate-200 !border-x-slate-200"
                                                    isScrollable={false}
                                                    tableContainerClass=""
                                                    // bodyItemClass="!border-slate-200"
                                                    expandedRowTClass="!z-[1300]"
                                                />
                                            </div>
                                        ) : (
                                            <div className="flex justify-center items-center h-full py-5">
                                                <span className="text-center text-sm font-rocGroteskMedium">
                                                    No Associated Products available
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        }
                        renderEmptyTable={() => (
                            <EmptyStateComp title="No data found" description="No data available" />
                        )}
                    />
                </div>

                <div className="flex justify-end mt-5">
                    <ResponsivePaginationComponent
                        current={Number(nonFinishedGoodsPagination?.currentPage)}
                        total={Number(nonFinishedGoodsPagination?.noOfPages)}
                        maxWidth={350}
                        onPageChange={(page) => {
                            nonFinishedGoodsSetUpdatedPage(page);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default SpaceDetailsForecastTabComponentForecast;
